import { memo, useCallback } from 'react'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import { trackAnnouncementInteraction } from '../../utils/analytics'
import { toast } from '../../services/toastService'
import { createLogger } from '../../utils/logger'

const logger = createLogger('AnnouncementCard')

/**
 * AnnouncementCard - Individual announcement display
 * Handles expand/collapse, sharing, and rendering of announcement content
 * 
 * @param {Object} announcement - Announcement data
 * @param {boolean} isExpanded - Whether card is expanded
 * @param {Function} onToggle - Callback to toggle expansion
 * @param {Function} onGenerateShareLink - Function to generate share link
 * @param {Function} getTypeIcon - Function to get icon component for type
 * @param {Function} getTypeColor - Function to get color classes for type
 * @param {Function} - Function to get priority indicator
 * @param {Function} formatDate - Function to format date
 * @param {Function} t - Translation function
 * @param {boolean} isPinned - Whether announcement is pinned
 * @param {Set} brokenAvatars - Set of announcement IDs with broken avatars
 * @param {Function} setBrokenAvatars - Callback to update broken avatars
 */
const AnnouncementCard = memo(function AnnouncementCard({ 
  announcement, 
  isExpanded, 
  onToggle,
  onGenerateShareLink,
  getTypeIcon, 
  getTypeColor, 
  getPriorityIndicator,
  formatDate,
  t,
  isPinned,
  brokenAvatars,
  setBrokenAvatars
}) {
  const TypeIcon = getTypeIcon(announcement.announcementType)
  const typeColor = getTypeColor(announcement.announcementType)
  const priorityIndicator = getPriorityIndicator(announcement.priority)

  const truncateContent = (content, maxLength = 120) => {
    if (!content) return ''
    if (content.length <= maxLength) return content
    return content.substring(0, maxLength) + '...'
  }

  const shouldTruncate = announcement.content && announcement.content.length > 120

  const handleToggle = useCallback(() => {
    onToggle(announcement.id, announcement.title)
  }, [onToggle, announcement.id, announcement.title])

  const handleShare = useCallback(async () => {
    const shareLink = onGenerateShareLink(announcement)
    try {
      await navigator.clipboard.writeText(shareLink)
      trackAnnouncementInteraction(announcement.id, 'share', announcement.title)
      toast.success(t('announcements.linkCopied', 'Link copied to clipboard!'))
    } catch (err) {
      logger.error('Failed to copy link', err)
      toast.error(t('announcements.linkCopyFailed', 'Failed to copy link'))
    }
  }, [announcement, onGenerateShareLink, t])

  return (
    <div 
      id={`announcement-${announcement.id}`}
      className="bg-white/90 backdrop-blur-sm rounded-xl border border-gray-200/50 hover:border-primary-300 hover:shadow-lg transition-all group"
    >
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          {/* Icon */}
          <div className="flex items-center gap-2">
            <div className={`flex-shrink-0 w-9 h-9 rounded-lg border shadow-sm ${typeColor} flex items-center justify-center transition-transform group-hover:scale-105`}>
              <TypeIcon className="w-4 h-4" />
            </div>
          </div>
          
          {/* Title and Meta */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-1.5 mb-1">
              <h4 className="font-semibold text-sm text-gray-900 line-clamp-2 flex-1">
                {announcement.title}
              </h4>
              {/* Author Avatar */}
              {announcement.authorAvatar && !brokenAvatars.has(announcement.id) && (
                <a 
                  href="https://instagram.com/alce.dorado"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 hover:opacity-80 transition-opacity"
                  title={t('announcements.viewInstagram', 'Posted by {{author}} - View Instagram', { author: announcement.authorName || 'Admin' })}
                >
                  <img 
                    src={announcement.authorAvatar} 
                    alt={announcement.authorName || 'Admin'} 
                    className="w-6 h-6 rounded-full object-cover border border-gray-200"
                    onError={() => setBrokenAvatars(prev => new Set(prev).add(announcement.id))}
                    referrerPolicy="no-referrer"
                  />
                </a>
              )}
              {priorityIndicator}
              {isPinned && (
                <div className="flex-shrink-0 px-2 py-0.5 bg-primary text-white rounded-full text-xs font-bold shadow-sm">
                  Pinned
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap text-xs text-gray-500">
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border ${typeColor}`}>
                {t(`announcements.types.${announcement.announcementType}`, announcement.announcementType)}
              </span>
              {announcement.publishedAt && (
                <span>{formatDate(announcement.publishedAt)}</span>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="text-gray-700 text-xs leading-relaxed pl-10.5">
          {isExpanded ? (
            <div className="whitespace-pre-wrap">{announcement.content}</div>
          ) : (
            <div>{truncateContent(announcement.content)}</div>
          )}
        </div>

        {/* Actions Row */}
        <div className="mt-3 ml-10.5 flex items-center gap-2 flex-wrap">
          {/* Expand/Collapse Button */}
          {shouldTruncate && (
            <button
              onClick={handleToggle}
              onTouchEnd={handleToggle}
              className="inline-flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold text-primary bg-white/90 backdrop-blur-sm border border-primary-200/50 hover:bg-white hover:border-primary-300 hover:shadow-md rounded-lg transition-all duration-200"
            >
              {isExpanded ? (
                <>
                  <span>{t('announcements.collapse', 'Show less')}</span>
                  <ChevronUpIcon className="w-3.5 h-3.5" />
                </>
              ) : (
                <>
                  <span>{t('announcements.readMore', 'Read more')}</span>
                  <ChevronDownIcon className="w-3.5 h-3.5" />
                </>
              )}
            </button>
          )}

          {/* Share Button */}
          <button
            onClick={handleShare}
            onTouchEnd={handleShare}
            className="inline-flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium text-gray-700 bg-white/90 backdrop-blur-sm border border-gray-200/50 hover:bg-white hover:border-gray-300 hover:shadow-md rounded-lg transition-all duration-200"
            title={t('announcements.shareLink', 'Copy share link')}
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
            <span>{t('announcements.share', 'Share')}</span>
          </button>
        </div>
      </div>
    </div>
  )
})

export default AnnouncementCard
